// admin/screens/ratings/Rate.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTopRated, fetchAllRatedContents, type TopRatedItem, type RatedContent } from '../../../../api/rate';
import '../../../../assets/styles/rate.scss'

type SortKey = 'best' | 'worst' | 'count_desc' | 'count_asc' | 'newest' | 'oldest';
type TypeFilter = 'TEXT' | 'BOTH' | 'ALL';
type PremiumFilter = 'any' | 'true' | 'false';

const PAGE_SIZE = 20;

function formatRating(avg?: number | null, count?: number | null) {
    if (!avg || !count) return '—';
    const v = Math.round(avg * 10) / 10; // 1 miejsce po przecinku
    return `${v} (${count})`;
}

function RatingPill({ avg, count }: { avg?: number | null; count?: number | null }) {
    const text = formatRating(avg, count);
    const ok = !!avg && !!count;
    return (
        <span className={`badge ${ok ? 'badge-success' : 'badge-muted'}`} title={ok ? 'Średnia (liczba ocen)' : 'Brak ocen'}>
            {text}
        </span>
    );
}

export default function Rate() {
    const navigate = useNavigate();

    // dane
    const [topWeek, setTopWeek] = useState<TopRatedItem[]>([]);
    const [topAll, setTopAll] = useState<TopRatedItem[]>([]);
    const [allRated, setAllRated] = useState<RatedContent[]>([]);

    // stany UI
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    // filtry
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('TEXT');
    const [premiumFilter, setPremiumFilter] = useState<PremiumFilter>('any');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [minCount, setMinCount] = useState<number>(0);
    const [sort, setSort] = useState<SortKey>('best');
    const [page, setPage] = useState(1);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setErr(null);
            try {
                const [w, a, all] = await Promise.all([
                    fetchTopRated('week'),
                    fetchTopRated('all'),
                    fetchAllRatedContents(),
                ]);
                setTopWeek(w);
                setTopAll(a);
                setAllRated(all);
            } catch (e: any) {
                setErr(e?.error || e?.message || 'Nie udało się pobrać rankingów');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const categories = useMemo(() => {
        const s = new Set<string>();
        allRated.forEach((c) => { if (c.category_title) s.add(c.category_title); });
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [allRated]);

    const filtered = useMemo(() => {
        const qLower = q.trim().toLowerCase();
        let list = allRated.filter((c) => {
            const matchQ = !qLower || c.title.toLowerCase().includes(qLower) || String(c.id).includes(qLower);
            const matchType =
                typeFilter === 'ALL' ? true : (typeFilter === 'TEXT' ? (c.type === 'TEXT' || c.type === 'BOTH') : c.type === 'BOTH'); // domyślnie „teksty”
            const matchPremium =
                premiumFilter === 'any' ? true : premiumFilter === 'true' ? !!c.is_premium : !c.is_premium;
            const matchCategory = !categoryFilter || (c.category_title || '') === categoryFilter;
            const matchMinCnt = (c.ratings_count ?? 0) >= minCount;
            return matchQ && matchType && matchPremium && matchCategory && matchMinCnt;
        });

        switch (sort) {
            case 'best':
                list = list.sort((a, b) => (b.avg_rating ?? 0) - (a.avg_rating ?? 0) || (b.ratings_count ?? 0) - (a.ratings_count ?? 0));
                break;
            case 'worst':
                list = list.sort((a, b) => (a.avg_rating ?? 0) - (b.avg_rating ?? 0) || (a.ratings_count ?? 0) - (b.ratings_count ?? 0));
                break;
            case 'count_desc':
                list = list.sort((a, b) => (b.ratings_count ?? 0) - (a.ratings_count ?? 0) || (b.avg_rating ?? 0) - (a.avg_rating ?? 0));
                break;
            case 'count_asc':
                list = list.sort((a, b) => (a.ratings_count ?? 0) - (b.ratings_count ?? 0) || (a.avg_rating ?? 0) - (b.avg_rating ?? 0));
                break;
            case 'newest':
                list = list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                break;
            case 'oldest':
                list = list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                break;
        }
        return list;
    }, [allRated, q, typeFilter, premiumFilter, categoryFilter, minCount, sort]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => { setPage(1); }, [q, typeFilter, premiumFilter, categoryFilter, minCount, sort]);

    return (
        <div className="admin-root" style={{ gap: 16 }}>
            <header className="admin-header">
                <h1>Rankingi i Oceny</h1>
            </header>

            {/* TOP karty */}
            <section className="grid-2">
                <div className="card">
                    <h3 style={{ marginTop: 0, marginBottom: 8 }}>Top 10 – ostatni tydzień</h3>
                    {loading ? <div className="muted">Ładowanie…</div> : (
                        <ol className="list" style={{ marginTop: 0 }}>
                            {topWeek.length === 0 && <div className="muted">Brak danych</div>}
                            {topWeek.map((it, idx) => (
                                <li key={it.content_id} className="list-item" style={{ justifyContent: 'flex-start', gap: 10 }}>
                                    <span className="mono" style={{ width: 22, textAlign: 'right' }}>{idx + 1}.</span>
                                    <span className="strong" style={{ flex: 1, minWidth: 0 }}>{it.title}</span>
                                    <RatingPill avg={it.avg_rating} count={it.ratings_count} />
                                    <button className="btn" onClick={() => navigate(`/admin/contents/edit/${it.content_id}`)}>Edytuj</button>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>

                <div className="card">
                    <h3 style={{ marginTop: 0, marginBottom: 8 }}>Top 10 – cały czas</h3>
                    {loading ? <div className="muted">Ładowanie…</div> : (
                        <ol className="list" style={{ marginTop: 0 }}>
                            {topAll.length === 0 && <div className="muted">Brak danych</div>}
                            {topAll.map((it, idx) => (
                                <li key={it.content_id} className="list-item" style={{ justifyContent: 'flex-start', gap: 10 }}>
                                    <span className="mono" style={{ width: 22, textAlign: 'right' }}>{idx + 1}.</span>
                                    <span className="strong" style={{ flex: 1, minWidth: 0 }}>{it.title}</span>
                                    <RatingPill avg={it.avg_rating} count={it.ratings_count} />
                                    <button className="btn" onClick={() => navigate(`/admin/contents/edit/${it.content_id}`)}>Edytuj</button>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </section>

            {/* FILTRY */}
            <section className="card">
                <div className="form-row" style={{ marginBottom: 0 }}>
                    <div className="field">
                        <label>Szukaj</label>
                        <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tytuł lub ID…" />
                    </div>

                    <div className="field">
                        <label>Typ treści</label>
                        <select className="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}>
                            <option value="TEXT">Tylko teksty</option>
                            <option value="BOTH">Teksty + audio (BOTH)</option>
                            <option value="ALL">Wszystkie typy</option>
                        </select>
                    </div>

                    <div className="field">
                        <label>Premium</label>
                        <select className="select" value={premiumFilter} onChange={(e) => setPremiumFilter(e.target.value as PremiumFilter)}>
                            <option value="any">Dowolne</option>
                            <option value="true">Tylko premium</option>
                            <option value="false">Bez premium</option>
                        </select>
                    </div>

                    <div className="field">
                        <label>Kategoria</label>
                        <select className="select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                            <option value="">Wszystkie</option>
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    <div className="field">
                        <label>Min. liczba ocen</label>
                        <input className="input" type="number" min={0} value={minCount} onChange={(e) => setMinCount(Math.max(0, Number(e.target.value) || 0))} />
                    </div>

                    <div className="field">
                        <label>Sortowanie</label>
                        <select className="select" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                            <option value="best">Najlepsze (↑ średnia)</option>
                            <option value="worst">Najgorsze (↓ średnia)</option>
                            <option value="count_desc">Najwięcej ocen</option>
                            <option value="count_asc">Najmniej ocen</option>
                            <option value="newest">Najnowsze</option>
                            <option value="oldest">Najstarsze</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* LISTA WSZYSTKICH */}
            <section className="card">
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: 70 }}>ID</th>
                                <th>Tytuł</th>
                                <th>Śr. ocen</th>
                                <th>Liczba</th>
                                <th>Typ</th>
                                <th>Kategoria</th>
                                <th>Premium</th>
                                <th>Data</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9}>Ładowanie…</td></tr>
                            ) : pageItems.length === 0 ? (
                                <tr><td colSpan={9} className="empty">Brak wyników</td></tr>
                            ) : pageItems.map((c) => (
                                <tr key={c.id}>
                                    <td className="mono">{c.id}</td>
                                    <td className="ellipsis" title={c.title}>{c.title}</td>
                                    <td className="strong"><RatingPill avg={c.avg_rating} count={c.ratings_count} /></td>
                                    <td className="mono">{c.ratings_count ?? 0}</td>
                                    <td>{c.type}</td>
                                    <td>{c.category_title || '—'}</td>
                                    <td>
                                        <span className={`badge ${c.is_premium ? 'badge-success' : 'badge-muted'}`}>{c.is_premium ? 'TAK' : 'NIE'}</span>
                                    </td>
                                    <td>{new Date(c.created_at).toLocaleDateString()}</td>
                                    <td className="actions">
                                        <button className="btn btn-ghost" onClick={() => navigate(`/admin/contents/edit/${c.id}`)}>Edytuj</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginacja */}
                <div className="pagination">
                    <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>◀</button>
                    <span>Strona {page} / {totalPages}</span>
                    <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>▶</button>
                </div>
            </section>

            {err && <div className="alert error">{err}</div>}
        </div>
    );
}
